/**
 * Job Owner Guard — the edit, manage and applicants pages are for the job's
 * creator (or an admin) only. Before this, any paid member could open them
 * for someone else's job and see the owner controls (Edit, Reopen, Mark
 * Filled) and the applicant count; the rules refused the writes, but the
 * pages should not be offered. Use inside JobAccessGuard.
 */

import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'wouter';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useFirebaseAuth } from '@/hooks/use-firebase-auth';
import { hasAnyRole } from '@/lib/normalize-user-data';
import { jobPostingsService } from '@/lib/services';

type State = 'loading' | 'owner' | 'not-owner' | 'missing';

export function JobOwnerGuard({ children }: { children: React.ReactNode }) {
  const { user } = useFirebaseAuth();
  const [location] = useLocation();
  const jobId = location.match(/^\/jobs\/([^/]+)\//)?.[1] ?? '';
  const [state, setState] = useState<State>('loading');

  useEffect(() => {
    let cancelled = false;
    setState('loading');
    if (!jobId || !user?.id) return;
    jobPostingsService
      .getJob(jobId)
      .then((job) => {
        if (cancelled) return;
        if (!job) setState('missing');
        else if (job.creatorId === user.id || hasAnyRole(user, ['ADMIN'])) setState('owner');
        else setState('not-owner');
      })
      .catch(() => {
        if (!cancelled) setState('missing');
      });
    return () => {
      cancelled = true;
    };
  }, [jobId, user]);

  if (state === 'owner') return <>{children}</>;

  if (state === 'loading') {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="max-w-md text-center p-8">
        <h2 className="text-xl font-bold mb-3">
          {state === 'missing' ? 'Job not found' : 'This is not your job posting'}
        </h2>
        <p className="text-muted-foreground mb-6">
          {state === 'missing'
            ? 'This job posting does not exist or has been removed.'
            : 'Only the member who posted this job can edit it or see its applicants.'}
        </p>
        <div className="flex gap-3 justify-center">
          {state === 'not-owner' && (
            <Link href={`/jobs/${jobId}/view`}>
              <Button variant="outline">View the job</Button>
            </Link>
          )}
          <Link href="/my-jobs">
            <Button>My jobs</Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
